import React, { useState, useEffect } from 'react'
import { Box, Button, Icon, Input, Text, Textarea } from '@maui/elements'
import { FileAttachments } from '../common/FileAttachments'
import { RequiredIndicator } from '../common/RequiredIndicator'
import { StepNavigator } from '../common/StepNavigator'
import { useEodDrawer } from '../../providers/EodDrawerContext'
import isEmpty from 'lodash/isEmpty'

// https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
const formatBytes = (bytes, decimals = 1) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['bytes', 'kb', 'mb', 'gb']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

const truncate = (string, limit = 10) => {
  if (string.length <= limit) {
    return string
  }
  return string.slice(0, limit) + '...'
}

function FileDropThumbnailReadOnly({ files = [] }) {
  if (!files?.length) {
    return null
  }

  return files.map((file, index) => (
    <Box key={index} display='inline-flex' borderRadius='radiusMedium' width='72px' height='72px' overflow='hidden' mr='4px' mb='4px'>
      <Box backgroundColor='colorBackgroundOverlay' style={{ width: '100%', backgroundSize: 'cover', position: 'relative' }}>
        <Box backgroundColor='transparent' border='none' color='colorActionDestructive' position='absolute' right={0} top={0}>
        </Box>
        <Box display='flex' alignItems='center' justifyContent='center' flexDirection='column' height='100%'>
          {file?.file_size && <Text mb='2px' fontSize='10px' backgroundColor='colorFillDeemphasis' padding='2px' borderRadius='radiusMedium'>{formatBytes(file.file_size)}</Text>}
          {file?.file_name && <Text fontSize='8px' backgroundColor='colorFillDeemphasis' padding='2px' borderRadius='radiusMedium'>{truncate(file.file_name)}</Text>}
        </Box>
      </Box>
    </Box>
  ))
}

export default function FreeformRequest() {
  const { dispatch, state, mode, maApiHost, authenticatedFetch } = useEodDrawer()
  const [titleInput, setTitleInput] = useState('')
  const [descriptionInput, setDescriptionInput] = useState('')
  const fields = state.requests.freeform.fields
  const { handleFieldChange, onSubmit, onClose, uploadFiles, removeUploadedFile } = dispatch
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (uploadFiles && !isEmpty(state.withFileHash)) {
      uploadFiles([state.withFileHash])
    }
  }, [state.withFileHash, uploadFiles])

  const onSubmitClick = () => {
    onSubmit(setIsSubmitting)
  }
  return (
    <>
      {mode === 'drawer' &&
        <StepNavigator>
          <Box display='flex' flex='1' alignItems='center'>
            <Button variant='secondary' onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button variant='secondary' onClick={() => dispatch.setRequestType('new')}>
            Back
          </Button>
          { isSubmitting && <Button disabled marginLeft='spaceGapLarge'>Submitting...</Button> }
          { !isSubmitting && <Button marginLeft='spaceGapLarge' onClick={onSubmitClick} disabled={(!descriptionInput || !titleInput) && true}>Submit</Button> }
        </StepNavigator>
      }

      <Box marginTop='16px' marginBottom='16px'>
        <Text>
          Provide us with a brief description of the situation, including what is most critical for you, a timeline of
          events, and what you require from our analyst team. You may upload supporting evidence as needed. Responses
          may take up to 48 hours.
        </Text>
      </Box>

      <form onSubmit={onSubmit}>
        <Box marginBottom="24px">
          <Text as="label" variant="body2" display="block" marginBottom="8px" htmlFor='titleField'>
            Title <RequiredIndicator />
          </Text>
          <Input
            id='titleField'
            name='subject'
            size='medium'
            width='100%'
            placeholder='Enter a title for your request...'
            onKeyUp={handleFieldChange}
            onChange={(e) => {
              setTitleInput(e.target.value)
            }}
            defaultValue={fields.subject.value}
            borderColor={!fields.subject.valid ? 'colorFeedbackError' : 'colorTextMain'}
          />
        </Box>
        <Box marginBottom="24px">
          <Text as="label" variant="body2" display="block" marginBottom="8px" htmlFor='descriptionField'>
            Description <RequiredIndicator />
          </Text>
          <Textarea
            id='descriptionField'
            name='description'
            height='375px'
            width='100%'
            placeholder='Enter the description of your request...'
            onKeyUp={handleFieldChange}
            onChange={(e) => {
              setDescriptionInput(e.target.value)
            }}
            defaultValue={fields.description.value}
            borderColor={!fields.description.valid ? 'colorFeedbackError' : 'colorTextMain'}

          />
        </Box>
        {!isEmpty(state.withFileHash)
          ? <Box mb="spaceGapXLarge">
            <Box mb="spaceGapMedium">
              <Text variant="heading5" as='label' htmlFor='attachmentInput'>
                  File Attachment
              </Text>
            </Box>
            <Text variant="body2" mb="spaceGapXLarge">
                The attached file is from the file analysis page.
            </Text>
            <Box
              maxWidth='472px'
              borderRadius="radiusLarge"
              height="100%"
              border='2px solid #52637A'
              pt='spaceGapLarge'
              pb='spaceGapLarge'
              pl='spaceGapMedium'
              pr='spaceGapMedium'
            >
              <FileDropThumbnailReadOnly files={state.requests?.freeform?.fields?.customer_attachments?.value} />
            </Box>
          </Box>
          : <FileAttachments
            onDropSuccess={arrayOfFiles => uploadFiles(arrayOfFiles, maApiHost, authenticatedFetch)}
            onFileRemoved={file => removeUploadedFile(file)}
          />}
        <Text variant='body2' marginTop='4px'></Text>
        <Box mb='spaceGapXLarge'>
          <Text variant='body2' mb='spaceGapXLarge' color='colorTextDeemphasis'>
            <RequiredIndicator /> Required fields
          </Text>
        </Box>
      </form>
      {mode === 'page' &&
        <Box display="flex" gap="12px" mt="16px">
          <Button
            variant="secondary"
            onClick={() => dispatch.setRequestType('new')}
            display="flex"
            alignItems="center"
            gap="10px"
          >
            <Icon name='left-arrow' size="sm" /> Back
          </Button>
          <Button
            onClick={isSubmitting ? undefined : onSubmitClick}
            disabled={isSubmitting || !descriptionInput || !titleInput}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </Box>
      }
    </>
  )
}
